import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, filter, finalize, map, takeUntil, takeWhile, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';

import {ProfileService} from '@services/profile.service';
import {Education} from '@models/education';
import {EducationCertificateModalComponent} from '../../education-certificate-modal/education-certificate-modal.component';
import {CertificatesService} from '@services/certificates.service';
import {ClientMessageService} from '@services/client-message.service';
import {ProfileEntity} from '@models/user/profile.entity';

@Component({
  selector: 'app-user-education-list',
  templateUrl: './user-education-list.component.html',
  styleUrls: ['./user-education-list.component.scss'],
})
export class UserEducationListComponent implements OnInit, OnDestroy {
  public MAX_CERTIFICATES_NUMBER: number = 10;

  trainer: ProfileEntity;
  cetrificates: Education[] = [];
  certificatesFormArray: UntypedFormArray;
  formName = 'educations';
  maxlength = 100;

  public canEditProfile: boolean;
  private destroy$ = new Subject<void>();
  private subs: Subscription;

  constructor(
    private profileService: ProfileService,
    private certificateService: CertificatesService,
    private clientMessageService: ClientMessageService,
  ) {
    this.canEditProfile = true;
    this.subs = new Subscription();
  }

  ngOnInit() {
    this.subs.add(
      this.profileService.editProfileEvent.subscribe(event => {
        this.canEditProfile = event;
      }),
    );
    this.getEducations();
    this.formInit(this.cetrificates);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Get all educations
   * Filtered them by system-setrificate-id
   */
  getEducations() {
    this.profileService.trainer
      .pipe(
        tap((trainer: ProfileEntity) => {
          this.trainer = trainer;
        }),
        map((trainer: ProfileEntity) => {
          return trainer.educations;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result: Education[]) => {
        this.cetrificates = result;
      });
  }

  /**
   * Generate form from certificates list
   */
  formInit(cetrificates: Education[]) {
    const cetrificatesControls = cetrificates.map((certificate: Education) => {
      return new UntypedFormGroup({
        id: new UntypedFormControl(certificate.id, [Validators.required]),
        title: new UntypedFormControl(certificate.title, [Validators.required]),
        file_link: new UntypedFormControl(certificate.file_link, [Validators.required]),
        file: new UntypedFormControl(''),
      });
    });

    this.certificatesFormArray = new UntypedFormArray(cetrificatesControls);

    this.certificatesFormArray.controls.forEach((control: UntypedFormGroup) => {
      this.listenCertificateChanges(control);
    });

    // if there are no any certificates we need add one
    if (this.certificatesFormArray.length < 1) {
      this.addCertificate();
    }
  }

  /**
   * Get certificate ID value
   */
  getCertificateIdValue(certificate: UntypedFormGroup) {
    return certificate.get('id').value;
  }

  /**
   * Get certificate title control
   */
  getCertificateTitleControl(certificate: UntypedFormGroup) {
    return certificate.get('title');
  }

  /**
   * Get certificate image value
   */
  getCertificateImageControlValue(certificate: UntypedFormGroup) {
    return certificate.get('file_link').value;
  }

  /**
   * Get certificate image url
   */
  getCertificateImageUrl(certificate: UntypedFormGroup) {
    const url = certificate.get('file_link').value;
    return url ? `url(${url})` : '';
  }

  /**
   * Get certificate image control
   */
  getCertificateImageControl(certificate: UntypedFormGroup) {
    return certificate.get('file_link');
  }

  /**
   * Add certificate
   */
  addCertificate() {
    const newCertificateGroup = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      title: new UntypedFormControl('', [Validators.required]),
      file_link: new UntypedFormControl('', [Validators.required]),
      file: new UntypedFormControl(''),
    });

    this.certificatesFormArray.push(newCertificateGroup);

    // add listener
    this.listenCertificateChanges(newCertificateGroup);
  }

  /**
   * Add listener to certificate
   */
  listenCertificateChanges(certificateGroup: UntypedFormGroup) {
    certificateGroup.valueChanges
      .pipe(
        tap(() => {
          this.certificateService.markFormGroupTouched(certificateGroup);
        }),
        takeWhile(() => {
          // trick for unsubscribe from this FormGroup by takeWhile
          return !certificateGroup.contains('remove');
        }),
        filter(() => {
          return certificateGroup.valid;
        }),
        debounceTime(500),
        takeUntil(this.destroy$),
      )
      .subscribe((result: any) => {
        if (Number.isInteger(result.id)) {
          this.updateCertificate(result, certificateGroup);
        } else {
          this.createCertificate(result, certificateGroup);
        }
      });
  }

  /**
   * Update certificate data
   */
  updateCertificate(certificateData, certificateGroup: UntypedFormGroup) {
    const formData = this.getFormData(certificateData);

    this.certificateService.update(this.formName, formData, certificateData.id)
      .pipe(
        finalize(() => {
          certificateGroup.enable({
            emitEvent: false,
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // save new certificates list
        const extractedData = this.extractData(this.certificatesFormArray);
        this.profileService.setEducations(extractedData);
      }, (error) => {
        this.handleErrorResponse(error);
      });
  }

  /**
   * Update certificate data
   */
  createCertificate(certificateData, certificateGroup: UntypedFormGroup) {
    const formData = this.getFormData(certificateData);

    this.certificateService.create(this.formName, formData)
      .pipe(
        finalize(() => {
          certificateGroup.enable({
            emitEvent: false,
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result: Education) => {
        // set id for certificateGroup
        certificateGroup.patchValue({
          id: result.id,
        }, {
          emitEvent: false,
        });
        // save new certificates list
        const extractedData = this.extractData(this.certificatesFormArray);
        this.profileService.setEducations(extractedData);
      }, (error) => {
        this.handleErrorResponse(error);
      });
  }

  /**
   * Remove sertificate
   */
  removeCertificate(index: number, certificateGroup: UntypedFormGroup) {
    const id = this.getCertificateIdValue(certificateGroup);

    // trick for unsubscribe from this FormGroup by takeWhile
    certificateGroup.setControl('remove', new UntypedFormControl());

    if (id === null) {
      this.removeCertificateByIndex(index);
    } else {
      this.removeCertificateById(certificateGroup, id, index);
    }
  }

  /**
   * Remove sertificate by index from client
   */
  removeCertificateByIndex(index) {
    this.certificatesFormArray.removeAt(index);

    // if there are no any certificates we need add one
    if (this.certificatesFormArray.length < 1) {
      this.addCertificate();
    }
  }

  /**
   * Remove sertificate by ID from server
   * if successfull then remove on client
   */
  removeCertificateById(certificateGroup: UntypedFormGroup, id, index) {
    certificateGroup.disable({
      emitEvent: false,
    });

    this.certificateService.remove(this.formName, id)
      .pipe(
        finalize(() => {
          certificateGroup.enable({
            emitEvent: false,
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.certificatesFormArray.removeAt(index);
        // save new certificates list
        const extractedData = this.extractData(this.certificatesFormArray);
        this.profileService.setEducations(extractedData);

        // if there are no any certificates we need add one
        if (this.certificatesFormArray.length < 1) {
          this.addCertificate();
        }
      }, (error) => {
        this.handleErrorResponse(error);
      });
  }

  /**
   * Get FormData for certificate
   */
  getFormData(certificate): FormData {
    const { id, file, title } = certificate;
    const formData = new FormData();

    if (!Number.isInteger(id)) {
      formData.append('profile_id', this.trainer.id.toString());
    }

    if (file) {
      formData.append('file_link', file);
    }

    if (title) {
      formData.append('title', title);
    }

    return formData;
  }

  /**
   * Open modal for view/change certificate
   */
  openCertificateModal(imageInput, certificateGroup: UntypedFormGroup): void {
    if (!this.canEditProfile) {
      this.certificateService.openCertificateModal(imageInput, certificateGroup, EducationCertificateModalComponent);
    }
  }

  /**
   * Emit when image field was changed
   */
  imageUpload(event, certificate: UntypedFormGroup): void {
    this.certificateService.imageUpload(event, certificate);
  }

  /**
   * Handle http errors
   */
  handleErrorResponse(errorResponse: HttpErrorResponse) {
    const { data, message, code } = errorResponse.error;

    console.error(errorResponse.error);
  }

  /**
   * Extract data for save it to global user
   */
  extractData(formArray: UntypedFormArray): Education[] {
    const certificates: Education[] = formArray.controls
      .map((formGroup: UntypedFormGroup): Education => {
        return {
          id: formGroup.get('id').value,
          file_link: formGroup.get('file_link').value || '',
          title: formGroup.get('title').value || '',
        };
      });

    return certificates;
  }

}
